import { api } from '@/lib/api';
import type { ActiveSeason, OverdueSowingItem, SeasonSummary } from './types';

interface ActiveSeasonResponse {
  season_id: string;
  code: string;
  year: number;
  starts_on: string;
  ends_on: string;
  as_of_date: string;
  is_current: boolean;
  is_future?: boolean;
  edit_unlock: boolean;
}

function toActiveSeason(r: ActiveSeasonResponse): ActiveSeason {
  return {
    seasonId: r.season_id,
    code: r.code,
    year: r.year,
    startsOn: r.starts_on,
    endsOn: r.ends_on,
    asOfDate: r.as_of_date,
    isCurrent: r.is_current,
    isFuture: r.is_future ?? false,
    editUnlock: r.edit_unlock,
  };
}

export async function fetchActiveSeason(): Promise<ActiveSeason | null> {
  try {
    const raw = await api.get<ActiveSeasonResponse>('/api/v1/session/season');
    return toActiveSeason(raw);
  } catch {
    return null;
  }
}

interface SetSeasonResp {
  season_id: string;
  code: string;
  edit_unlock: boolean;
}

export async function setActiveSeason(
  seasonCode: string,
  editUnlock?: boolean,
): Promise<SetSeasonResp> {
  return api.post<SetSeasonResp>('/api/v1/session/season', {
    season_code: seasonCode,
    ...(editUnlock !== undefined ? { edit_unlock: editUnlock } : {}),
  });
}

export async function setEditUnlock(
  enabled: boolean,
): Promise<{ edit_unlock: boolean }> {
  return api.post<{ edit_unlock: boolean }>(
    '/api/v1/session/season/edit-unlock',
    { enabled },
  );
}

interface SeasonRaw {
  id: string;
  company_id: string;
  code: string | null;
  name: string | null;
  year: number;
  starts_on: string | null;
  ends_on: string | null;
  status: string;
  is_current: boolean;
}

export async function listSeasons(companyId?: string): Promise<SeasonSummary[]> {
  const qs = companyId ? `?company_id=${companyId}` : '';
  const raw = await api.get<SeasonRaw[]>(`/api/v1/seasons${qs}`);
  return raw
    .filter((r) => r.code && r.starts_on && r.ends_on)
    .map((r) => ({
      id: r.id,
      code: r.code as string,
      year: r.year,
      startsOn: r.starts_on as string,
      endsOn: r.ends_on as string,
      isCurrent: r.is_current,
    }))
    .sort((a, b) => b.year - a.year);
}

export async function fetchOverdueSowings(): Promise<OverdueSowingItem[]> {
  try {
    return await api.get<OverdueSowingItem[]>(
      '/api/v1/dashboard/overdue-sowings',
    );
  } catch {
    return [];
  }
}
